// Types
import { Order } from "@/types/supabase";

// Libs
import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

// Hooks

// Components
import { Button } from "@/components/atoms/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
  DialogClose,
} from "@/components/atoms/dialog";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/atoms/avatar";
import { Badge } from "@/components/atoms/badge";
import { Checkbox } from "@/components/atoms/checkbox";
import DriverAssignmentConfirmation from "./DriverAssignmentConfirmation";
import { useAssignation } from "@/hooks/useAssignation";

interface IdleDriverResponse {
  id: number;
  name: string | null;
  branch_id: number | null;
  vehicle_type: string | null;
  worker_type: string | null;
  picture: string | null;
  is_checked_in?: boolean;
}

export default function OrderAssignDriver({
  trigger,
  order,
  onClose,
}: {
  trigger: React.ReactNode;
  order: Order;
  onOrderDriverUpdate?: (orderId: number, driver?: any) => void;
  onClose?: () => void;
}) {
  const { getAvailableDriversByBranch } = useAssignation();
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const requestId = useRef(0);
  const [selectedDriver, setSelectedDriver] =
    useState<IdleDriverResponse | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [drivers, setDrivers] = useState<IdleDriverResponse[]>([]);

  const getAvailableDrivers = async () => {
    const currentRequest = ++requestId.current;
    setIsLoading(true);
    setLoadError(false);
    setDrivers([]);
    try {
      const data = await getAvailableDriversByBranch(order.branch_id);
      if (currentRequest === requestId.current) {
        setDrivers(data as IdleDriverResponse[]);
      }
    } catch (error) {
      console.error("Error fetching available drivers:", error);
      if (currentRequest === requestId.current) {
        setLoadError(true);
      }
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false);
    }
  };

  const handleDriverSelect = (driver: IdleDriverResponse) => {
    setSelectedDriver(driver);
  };

  useEffect(() => {
    if (isDialogOpen) {
      setSelectedDriver(null);
      void getAvailableDrivers();
    }
    return () => {
      requestId.current += 1;
    };
  }, [isDialogOpen, order.branch_id]);

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-white" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="text-center">Asignar conductor</DialogTitle>
          <DialogDescription className="text-center">
            <span className="font-bold">Order {order.id}</span> -{" "}
            {order.branch?.name || "N/A"}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col h-[400px]">
          {/* Header fijo */}
          <div className="flex-shrink-0 text-indigo-700 font-medium text-center py-4 border-b border-slate-200">
            {isLoading ? (
              <LoadingSpinner />
            ) : loadError ? (
              "No pudimos cargar los conductores disponibles"
            ) : drivers.length > 0 ? (
              `${drivers.length} conductores disponibles en el radio`
            ) : (
              "No hay conductores disponibles en el radio"
            )}
          </div>

          {!isLoading && loadError && (
            <div className="flex flex-1 items-center justify-center">
              <Button
                variant="outline"
                onClick={() => void getAvailableDrivers()}
              >
                Reintentar
              </Button>
            </div>
          )}

          {/* Lista de conductores con scroll */}
          {!isLoading && drivers.length > 0 && (
            <div className="flex-1 overflow-y-auto">
              <ul className="divide-y divide-slate-200">
                {drivers.map((driver) => (
                  <li
                    key={driver.id}
                    className={clsx(
                      "py-3 px-4 cursor-pointer transition-colors",
                      selectedDriver?.id === driver.id
                        ? "bg-blue-50 border-blue-200"
                        : "hover:bg-gray-50",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDriverSelect(driver);
                    }}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <Checkbox
                        checked={selectedDriver?.id === driver.id}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleDriverSelect(driver);
                        }}
                      />
                      <div className="flex items-center gap-2">
                        <Avatar>
                          <AvatarImage
                            src={
                              driver.picture ||
                              "/driver-illustration/profile-pic-default.svg"
                            }
                          />
                          <AvatarFallback>
                            <div className="w-8 h-8 rounded-full bg-gray-300 grid place-items-center text-xs">
                              {driver.name?.substring(0, 2)}
                            </div>
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm sm:text-base max-w-32 truncate block sm:max-w-44">
                          {driver.name}
                        </span>
                      </div>
                      <p className="ml-auto capitalize text-xs sm:text-sm">
                        {driver.vehicle_type === "car"
                          ? "Auto"
                          : driver.vehicle_type === "motorcycle" ||
                              driver.vehicle_type === "moto"
                            ? "Moto"
                            : driver.vehicle_type === "bicycle"
                              ? "Bicicleta"
                              : driver.vehicle_type || "N/A"}
                      </p>
                      <div className="w-28 flex justify-end">
                        <Badge variant="outline">
                          {driver.worker_type
                            ? driver.worker_type.charAt(0).toUpperCase() +
                              driver.worker_type.slice(1)
                            : "N/A"}
                        </Badge>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <div className="flex flex-col gap-1 sm:flex-row-reverse">
            {selectedDriver ? (
              <DriverAssignmentConfirmation
                trigger={
                  <Button
                    className="bg-indigo-700 flex-1 min-w-32 text-white hover:text-white hover:bg-indigo-600"
                    disabled={isLoading}
                  >
                    Asignar
                  </Button>
                }
                order={order}
                selectedDriver={selectedDriver}
                onClose={onClose}
              />
            ) : (
              <Button
                className="bg-indigo-700 flex-1 min-w-32 text-white hover:text-white hover:bg-indigo-600"
                disabled={true}
              >
                Asignar
              </Button>
            )}
            <DialogClose asChild onClick={onClose}>
              <Button variant="outline" className="flex-1 min-w-32">
                Cancelar
              </Button>
            </DialogClose>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
